import { useCallback, useEffect, useRef, useState } from "react";
import { apiPcfEstoque, type PcfAFinalizar, type PecasResp } from "../lib/api";
import { type Usuario } from "../lib/auth";
import { Cabecalho } from "./ProducaoPcf";
import { MsgModal } from "../components/MsgModal";
import { Confirma, Modal, enterTab, fN, paraInput, parseBR, soNum } from "../components/EstoqueUi";
import { useOrdenacao, ThOrdena } from "../hooks/useOrdenacao";

// pcf_finalizacao (legado novo C:\formsteel_d\pcf_finalizacao, menu producao!pcf_finalizacao): TELA-1 lista as PCFs
// 'AGUARDANDO CONF PCP' / 'FINALIZADA PARCIAL'; Consulta = TELA-2 (apontamentos); Finalizar = TELA-PCF com Peças
// Fabricadas (TELA-3: gravar/excluir, Baixa Parcial, Finalizar PCF) e Sucata/Pontas (TELA-5). Regras no servidor.

interface Props {
  usuario: Usuario;
  onVoltar: () => void;
  onImprimir: (numpcf: number) => void;
}

export function PcfFinalizacao({ usuario, onVoltar, onImprimir }: Props) {
  const [linhas, setLinhas] = useState<PcfAFinalizar[] | null>(null);
  const [sel, setSel] = useState<PcfAFinalizar | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [consulta, setConsulta] = useState(false);
  const [pecas, setPecas] = useState(false);
  const ord = useOrdenacao(linhas, { pos: (l) => l.posicao, pcf: (l) => l.numpcf, cli: (l) => l.nomfan, eta: (l) => l.etapa, ent: (l) => l.datent, sit: (l) => l.situac, ini: (l) => l.datini });

  const carregar = useCallback(async () => {
    try {
      setLinhas(await apiPcfEstoque.finalizacao());
    } catch (e) {
      setErro((e as Error).message);
    }
  }, []);
  useEffect(() => {
    void carregar();
  }, [carregar]);

  // botao-finalizar (f1:170-187): só 'AGUARDANDO CONF PCP' / 'FINALIZADA PARCIAL'
  function finalizar() {
    if (!sel) return;
    if (sel.situac !== "AGUARDANDO CONF PCP" && sel.situac !== "FINALIZADA PARCIAL")
      return setErro("Etapas da produção selecionada não foram finalizadas ainda!");
    setPecas(true);
  }

  return (
    <div className="app">
      <Cabecalho usuario={usuario} titulo="Finalização de PCF" sub="Peças fabricadas, pontas e sucata" onVoltar={onVoltar} />
      <MsgModal texto={erro} tipo="erro" onClose={() => setErro(null)} />
      <div className="pcf-form">
        <section className="pcf-sec">
          <div className="tabela-wrap">
            <table className="tabela-pcfs">
              <thead>
                <tr>
                  <ThOrdena ord={ord} col="pos" className="num">Posição</ThOrdena>
                  <ThOrdena ord={ord} col="pcf" className="num">PCF</ThOrdena>
                  <ThOrdena ord={ord} col="cli">Cliente</ThOrdena>
                  <ThOrdena ord={ord} col="eta">Etapa</ThOrdena>
                  <ThOrdena ord={ord} col="ent">Entrega</ThOrdena>
                  <ThOrdena ord={ord} col="sit">Situação</ThOrdena>
                  <ThOrdena ord={ord} col="ini">Início</ThOrdena>
                </tr>
              </thead>
              <tbody>
                {linhas && linhas.length === 0 && <tr><td colSpan={7} className="plan-vazio">Nenhuma PCF aguardando finalização.</td></tr>}
                {ord.linhas.map((l) => (
                  <tr key={l.numpcf} className={"row-clickable" + (sel?.numpcf === l.numpcf ? " sel" : "")} onClick={() => setSel(sel?.numpcf === l.numpcf ? null : l)}>
                    <td className="num">{l.posicao || ""}</td>
                    <td className="num">{l.numpcf}</td>
                    <td>{l.nomfan}</td>
                    <td>{l.etapa}</td>
                    <td>{l.datent}</td>
                    <td>{l.situac}</td>
                    <td>{l.datini}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
        <div className="pcf-acoes">
          <button className="btn-acao" disabled={!sel} onClick={finalizar}>Finalizar</button>
          <button className="btn-secundario" disabled={!sel} onClick={() => setConsulta(true)}>Consulta</button>
          <span className="spacer" />
          <button className="btn-secundario" onClick={onVoltar}>Sair</button>
        </div>
      </div>
      {consulta && sel && <ConsultaEtapas numpcf={sel.numpcf} onClose={() => setConsulta(false)} />}
      {pecas && sel && (
        <PecasFabricadas
          numpcf={sel.numpcf}
          onClose={(final) => {
            setPecas(false);
            setSel(null);
            void carregar();
            if (final) onImprimir(final);
          }}
        />
      )}
    </div>
  );
}

// TELA-2 (f2:298-367)
function ConsultaEtapas({ numpcf, onClose }: { numpcf: number; onClose: () => void }) {
  const [r, setR] = useState<Awaited<ReturnType<typeof apiPcfEstoque.consulta>> | null>(null);
  useEffect(() => {
    apiPcfEstoque.consulta(numpcf).then(setR).catch(() => setR([]));
  }, [numpcf]);
  return (
    <Modal titulo="Consulta da Produção" sub={`PCF ${numpcf}`} largura={900} onClose={onClose}>
      <div className="tabela-wrap" style={{ maxHeight: "60vh", overflow: "auto" }}>
        <table className="tabela-pcfs">
          <thead><tr><th className="num">Posição</th><th>Etapa</th><th>Situação</th><th>Início</th><th>Fim</th></tr></thead>
          <tbody>
            {(r?.length ?? 0) === 0 && <tr><td colSpan={5} className="plan-vazio">Nenhum apontamento.</td></tr>}
            {r?.map((x, i) => (
              <tr key={i}><td className="num">{x.posicao}</td><td>{x.etapa}</td><td>{x.situac}</td><td>{x.inicio}</td><td>{x.fim}</td></tr>
            ))}
          </tbody>
        </table>
      </div>
    </Modal>
  );
}

// TELA-PCF + TELA-3 (peças fabricadas) + TELA-5 (sucata/pontas)
function PecasFabricadas({ numpcf, onClose }: { numpcf: number; onClose: (finalImprimir?: number) => void }) {
  const [d, setD] = useState<PecasResp | null>(null);
  const [f, setF] = useState({ sequen: 0, compri: "", qtdpec: "", peso: "", codloc: "" });
  const [sp, setSp] = useState({ pesoPontas: "", codlocPontas: "", pesoSucata: "", codlocSucata: "" });
  const [erro, setErro] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [pergunta, setPergunta] = useState<"excluir" | "parcial" | "final" | null>(null);
  const [busy, setBusy] = useState(false);
  const raiz = useRef<HTMLDivElement>(null);
  const refCompri = useRef<HTMLInputElement>(null);

  const carregar = useCallback(async () => {
    try {
      const r = await apiPcfEstoque.pecas(numpcf);
      if (!r.ok) return setErro(r.erro ?? "PCF não encontrada.");
      setD(r);
      const s = r.sucataPontas;
      setSp({
        pesoPontas: paraInput(s.peso_pontas, 4), codlocPontas: s.codloc_pontas ? String(s.codloc_pontas) : "",
        pesoSucata: paraInput(s.peso_sucata, 4), codlocSucata: s.codloc_sucata ? String(s.codloc_sucata) : "",
      });
    } catch (e) {
      setErro((e as Error).message);
    }
  }, [numpcf]);
  useEffect(() => {
    void carregar();
  }, [carregar]);
  const limpar = () => {
    setF({ sequen: 0, compri: "", qtdpec: "", peso: "", codloc: "" });
    setTimeout(() => refCompri.current?.focus(), 30);
  };

  async function gravar() {
    setBusy(true);
    try {
      const r = await apiPcfEstoque.gravarPeca(numpcf, { sequen: f.sequen || undefined, compri: parseBR(f.compri), qtdpec: parseBR(f.qtdpec), peso: parseBR(f.peso), codloc: Number(f.codloc) || 0 });
      if (!r.ok) return setErro(r.erro ?? "Falha na gravação.");
      await carregar();
      limpar();
    } finally {
      setBusy(false);
    }
  }
  async function excluir() {
    setPergunta(null);
    const r = await apiPcfEstoque.excluirPeca(numpcf, f.sequen);
    if (!r.ok) return setErro(r.erro ?? "Falha na exclusão.");
    await carregar();
    limpar();
  }
  async function gravarSucata() {
    const r = await apiPcfEstoque.gravarSucata(numpcf, { pesoPontas: parseBR(sp.pesoPontas), codlocPontas: Number(sp.codlocPontas) || 0, pesoSucata: parseBR(sp.pesoSucata), codlocSucata: Number(sp.codlocSucata) || 0 });
    if (!r.ok) {
      setErro(r.erro ?? "Falha na gravação.");
      return false;
    }
    return true;
  }
  // ROTINA-FINALIZAR (f3:570-678): grava antes a sucata/pontas digitada (TELA-5) e finaliza (parcial ou total)
  async function finalizar(parcial: boolean) {
    setPergunta(null);
    setBusy(true);
    try {
      if (!(await gravarSucata())) return;
      const r = await apiPcfEstoque.finalizar(numpcf, parcial);
      if (!r.ok) return setErro(r.erro ?? "Falha na finalização.");
      const msg = `${parcial ? "Baixa parcial" : "PCF finalizada"}: ${r.lotes?.length ?? 0} lote(s) gerado(s)${r.lotes?.length ? " (" + r.lotes.join(", ") + ")" : ""}.`;
      if (parcial) {
        setOk(msg);
        await carregar();
      } else onClose(numpcf); // Finalizar imprime a PCF (f3:326-343)
    } finally {
      setBusy(false);
    }
  }

  const fin = !!d?.finalizada;
  const locs = d?.locais ?? [];
  const selLoc = (v: string, on: (x: string) => void) => (
    <select value={v} disabled={fin} onChange={(e) => on(e.target.value)}>
      <option value=""></option>
      {locs.map((l) => <option key={l.codloc} value={l.codloc}>{l.nomloc}</option>)}
    </select>
  );
  return (
    <Modal titulo="Plano de Controle de Fabricação - Peças Fabricadas" sub={d ? `PCF nº ${numpcf} · ${d.cab.nomcli} · ${d.cab.nompro} · Pedido ${d.cab.numped || "—"} · ${d.cab.tipo} · ${d.cab.situac}` : `PCF nº ${numpcf}`} largura={1100} onClose={() => onClose()}>
      <MsgModal texto={erro} tipo="erro" onClose={() => setErro(null)} />
      <MsgModal texto={ok} tipo="ok" onClose={() => setOk(null)} />
      <Confirma
        texto={pergunta === "excluir" ? "Confirma exclusão do lançamento?" : pergunta === "parcial" ? "Confirma a BAIXA PARCIAL? Os lançamentos sem lote entram no estoque." : pergunta === "final" ? "Confirma a FINALIZAÇÃO da PCF? Os lançamentos sem lote, pontas e sucata entram no estoque." : null}
        onSim={() => void (pergunta === "excluir" ? excluir() : finalizar(pergunta === "parcial"))}
        onNao={() => setPergunta(null)}
      />
      {d && (
        <p className="pcf-hint">Emissão {d.cab.datemi} · Entrega {d.cab.datent || "—"} · Vendedor {d.cab.nomven || "—"} · Emitente {d.cab.nomusu || "—"}</p>
      )}
      <div ref={raiz} onKeyDown={(e) => enterTab(e, raiz.current)}>
        <div className="pcf-grid">
          <label className="pcf-campo pcf-sm"><span>Comprimento</span><input ref={refCompri} autoFocus className="num" inputMode="decimal" disabled={fin} value={f.compri} onChange={(e) => setF({ ...f, compri: soNum(e.target.value, 4) })} /></label>
          <label className="pcf-campo pcf-sm"><span>Quantidade</span><input className="num" inputMode="numeric" disabled={fin} value={f.qtdpec} onChange={(e) => setF({ ...f, qtdpec: soNum(e.target.value, 0) })} /></label>
          <label className="pcf-campo pcf-sm"><span>Peso</span><input className="num" inputMode="decimal" disabled={fin} value={f.peso} onChange={(e) => setF({ ...f, peso: soNum(e.target.value, 4) })} /></label>
          <label className="pcf-campo"><span>Local de Estoque</span>{selLoc(f.codloc, (x) => setF({ ...f, codloc: x }))}</label>
          <button className="btn-acao" disabled={fin || busy} onClick={() => void gravar()}>Gravar</button>
          <button className="btn-secundario plan-del" disabled={fin || !f.sequen} onClick={() => setPergunta("excluir")}>Excluir</button>
          <button className="btn-secundario" onClick={limpar}>Limpar</button>
        </div>
      </div>
      <div className="tabela-wrap" style={{ marginTop: 10, maxHeight: "32vh", overflow: "auto" }}>
        <table className="tabela-pcfs">
          <thead><tr><th className="num">Posição</th><th className="num">Comprimento</th><th className="num">Quantidade</th><th className="num">Peso</th><th>Local de Estoque</th><th className="num">Lote</th></tr></thead>
          <tbody>
            {(d?.pecas.length ?? 0) === 0 && <tr><td colSpan={6} className="plan-vazio">Nenhuma peça lançada.</td></tr>}
            {d?.pecas.map((p) => (
              <tr key={p.sequen} className={"row-clickable" + (f.sequen === p.sequen ? " sel" : "")}
                onClick={() => !fin && !p.numlot && setF({ sequen: p.sequen, compri: paraInput(p.compri, 4), qtdpec: paraInput(p.qtdpec, 0), peso: paraInput(p.peso, 4), codloc: String(p.codloc || "") })}>
                <td className="num">{p.sequen}</td><td className="num">{fN(p.compri, 2)}</td><td className="num">{fN(p.qtdpec, 0)}</td>
                <td className="num">{fN(p.peso, 2)}</td><td>{p.nomloc}</td><td className="num">{p.numlot || ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <section className="pcf-sec" style={{ marginTop: 12 }}>
        <h3 style={{ margin: "0 0 8px", fontSize: 14 }}>Pontas e Sucata</h3>
        <div className="pcf-grid">
          <label className="pcf-campo pcf-sm"><span>Peso Pontas</span><input className="num" inputMode="decimal" disabled={fin || !!d?.sucataPontas.numlot_pontas} value={sp.pesoPontas} onChange={(e) => setSp({ ...sp, pesoPontas: soNum(e.target.value, 4) })} /></label>
          <label className="pcf-campo"><span>Local das Pontas</span>{selLoc(sp.codlocPontas, (x) => setSp({ ...sp, codlocPontas: x }))}</label>
          <label className="pcf-campo pcf-sm"><span>Peso Sucata</span><input className="num" inputMode="decimal" disabled={fin || !!d?.sucataPontas.numlot_sucata} value={sp.pesoSucata} onChange={(e) => setSp({ ...sp, pesoSucata: soNum(e.target.value, 4) })} /></label>
          <label className="pcf-campo"><span>Local da Sucata</span>{selLoc(sp.codlocSucata, (x) => setSp({ ...sp, codlocSucata: x }))}</label>
          <button className="btn-secundario" disabled={fin} onClick={async () => { if (await gravarSucata()) setOk("Pontas e sucata gravadas."); }}>Gravar Pontas/Sucata</button>
        </div>
        {(d?.sucataPontas.numlot_pontas || d?.sucataPontas.numlot_sucata) ? (
          <p className="pcf-hint">Lotes já gerados — pontas: {d?.sucataPontas.numlot_pontas || "—"} · sucata: {d?.sucataPontas.numlot_sucata || "—"}</p>
        ) : null}
      </section>
      <div className="pcf-acoes">
        <button className="btn-acao" disabled={fin || busy} onClick={() => setPergunta("parcial")}>Baixa Parcial</button>
        <button className="btn-acao verde" disabled={fin || busy} onClick={() => setPergunta("final")}>Finalizar PCF</button>
        <span className="spacer" />
        <button className="btn-secundario" onClick={() => onClose()}>Voltar</button>
      </div>
    </Modal>
  );
}
